// Inbound webhook keys — the mirror of types/outboundWebhooks.ts. "Inbound" is load-bearing:
// these are keys WE issue so an external system can post leads in, not credentials we hold.
//
// Shapes are generated from OrbitalX.Dto — see api-generated.d.ts. Status is narrowed here because
// the C# DTO declares it `string`; the create/update DTOs keep the generated shape.

import type {
    InboundWebhookSubscriptionUpdateDto as GeneratedInboundWebhookSubscriptionUpdateDto,
    InboundWebhookSubscriptionViewDto as GeneratedInboundWebhookSubscriptionViewDto,
    InboundWebhookSummaryDto as GeneratedInboundWebhookSummaryDto,
} from './api-generated'

export type { InboundWebhookSubscriptionCreateDto } from './api-generated'

export type InboundWebhookSubscriptionStatus = 'Active' | 'Paused'

/**
 * `WorkspaceId` is always set — a key writes into exactly one workspace. `LastReceivedUtc` is null
 * until a submission first authenticates with the key.
 */
export interface InboundWebhookSubscriptionViewDto
    extends Omit<GeneratedInboundWebhookSubscriptionViewDto, 'Status'> {
    Status: InboundWebhookSubscriptionStatus
}

/** Patch: omitted fields are left alone. WorkspaceId is absent on purpose — moving a live key to
 *  another workspace would silently redirect traffic, so that is a delete and reissue. */
export interface InboundWebhookSubscriptionUpdateDto
    extends Omit<GeneratedInboundWebhookSubscriptionUpdateDto, 'Status'> {
    Status?: InboundWebhookSubscriptionStatus | null
}

/** Returned only by create and rotate. The key exists in a response exactly once. */
export interface InboundWebhookKeyRevealDto {
    Subscription: InboundWebhookSubscriptionViewDto
    Key: string
}

/** Non-secret view for the workspace and web settings screens. */
export interface InboundWebhookSummaryDto extends Omit<GeneratedInboundWebhookSummaryDto, 'Status'> {
    Status: InboundWebhookSubscriptionStatus
}

// Reuses the same 100/800 light, 900/30 + 300 dark scale as the integration statuses, which
// deliberately never touches step 500 — that is the brand green, and tinting a status with it
// would collide with the primary buttons beside it.
export const INBOUND_WEBHOOK_STATUS_COLORS: Record<InboundWebhookSubscriptionStatus, string> = {
    Active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    Paused: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
}
